import {Select, Spin} from 'antd'
import {organization_fetchStatistics_Api} from 'apis'
import classNames from 'classnames'
import Pure from 'components/Pure'
import _ from 'lodash'
import Async from 'modules/asyncCache/components/Async'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useMemo} from 'react'
import {IoChevronDown,} from 'react-icons/io5'
import {nest, withProps} from 'recompose'
import {DelayRender} from 'views/Shared/components/DelayRender'
import {bindQueryParam, bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {deepTranslate, getResponseItem, NullAsDefault, renderSelf} from 'views/Shared'
import {familyBook_getFamilyBookStats_Api} from '../../../apis/familyBook'
import {getId, getType} from '../../../apis/model/base'
import {FamilyIcon} from '../../../components/Svgs/FamilyIconSvg'
import {WorkspaceFilterParams} from 'views/Workspace/WorkspaceProvider'
import WorkspaceContext from 'views/Workspace/WorkspaceContext'
import WorkspaceInsightsChart, {InsightsChartTypes,} from './WorkspaceInsightsChart'
import WorkspaceInsightsMetaSummary from './WorkspaceInsightsMetaSummary'
import WorkspaceParishOverviewSection from './WorkspaceParishOverviewSection'
import WorkspaceOrganizationAnnouncements from "./WorkspaceOrganizationAnnouncements";
import {useAnnouncementForm} from "../../AppLocals/Announcement/useAnnouncementForm";
import {BsCalendar2DayFill, BsChevronRight, BsPeopleFill} from "react-icons/bs";
import {FaGraduationCap, FaUserGroup} from "react-icons/fa6";
import {Link, useLocation, useRouteMatch} from "react-router-dom";
import {LayoutContext} from "../../../components/layouts/Default/LayoutContext";
import dayjs from "dayjs";

export const SummaryFilterOptions = Object.freeze({
    YESTERDAY: {
        label: 'yesterday',
        value: 'YESTERDAY',
    },
    TODAY: {
        label: 'today',
        value: 'TODAY',
    },
    LAST7DAYS: {
        label: 'last 7 days',
        value: 'LAST7DAYS',
    },
    THISMONTH: {
        label: 'this month',
        value: 'THISMONTH',
    },
    LASTMONTH: {
        label: 'last month',
        value: 'LASTMONTH',
    },
})

export const InsightsBlockHeader = ({
                                        title,
                                        style,
                                        children,
                                        className,
                                        renderTitle = renderSelf,
                                    }) => (
    <div
        style={style}
        className={classNames('flex items-center justify-between mb-3', className)}>
        <Translate>
            {(t) => (
                <div className="text-color-200 uppercase font-medium text-lg">
                    {renderTitle(t(title))}
                </div>
            )}
        </Translate>
        {children}
    </div>
)

export const PeriodFilterHeader = ({
                                       title,
                                       value,
                                       defaultValue,
                                       onSelect = NullAsDefault,
                                       options = [
                                           SummaryFilterOptions.YESTERDAY,
                                           SummaryFilterOptions.TODAY,
                                           SummaryFilterOptions.LAST7DAYS,
                                           SummaryFilterOptions.THISMONTH,
                                           SummaryFilterOptions.LASTMONTH,
                                       ],
                                   }) => {
    const t = useTranslate()

    const hasFilter = !_.isEmpty(options)

    const _options = useMemo(() => {
        return Array.from(options).map(deepTranslate(t))
    }, [t, options])

    return (
        <InsightsBlockHeader title={title}>
            {hasFilter && (
                <Select
                    size="small"
                    value={value}
                    options={_options}
                    onSelect={onSelect}
                    style={{width: 110}}
                    // defaultValue={defaultValue}
                    className="text-xs text-color-300"
                    suffixIcon={<IoChevronDown className="text-color-300"/>}
                />
            )}
        </InsightsBlockHeader>
    )
}

const InsightsCharts = ({
                            style,
                            data = [],
                            className,
                            isLoading = false,
                            organization_id,
                        }) => {
    const [chart_1, chart_2] = [
        InsightsChartTypes.TYPE_1,
        InsightsChartTypes.TYPE_2,
    ].map((name) => ['workspace', organization_id, name].join('/'))
    return (
        <div
            style={style}
            className={className}>
            <Spin spinning={!!isLoading}>
                <WorkspaceInsightsChart
                    type={InsightsChartTypes.TYPE_1}
                    chartInfo={{
                        id: chart_1,
                        idname: chart_1,
                        data_values: Array.from(data).map(({log_date, ...rest}) => ({
                            time: log_date,
                            articles: rest.total_articles,
                            asks: rest.total_asks,
                            polls: rest.total_polls,
                            reposts: rest.total_reposts,
                        })),
                        data_columns: [
                            {
                                id: 'time',
                                dataIndex: 'time',
                                type: 'date',
                            },
                            {
                                id: 'articles',
                                dataIndex: 'articles',
                                type: 'number',
                                valueType: 'value',
                            },
                            {
                                id: 'asks',
                                dataIndex: 'asks',
                                type: 'number',
                                valueType: 'value',
                            },
                            {
                                id: 'polls',
                                dataIndex: 'polls',
                                type: 'number',
                                valueType: 'value',
                            },
                            {
                                id: 'reposts',
                                dataIndex: 'reposts',
                                type: 'number',
                                valueType: 'value',
                            },
                        ],
                    }}
                />
            </Spin>
            <Spin spinning={!!isLoading}>
                <WorkspaceInsightsChart
                    type={InsightsChartTypes.TYPE_2}
                    chartInfo={{
                        id: chart_2,
                        idname: chart_2,
                        data_values: Array.from(data).map(({log_date, ...rest}) => ({
                            time: log_date,
                            views: rest.total_views,
                            votes: rest.total_votes,
                            follows: rest.total_follows,
                            comments: rest.total_comments,
                        })),
                        data_columns: [
                            {
                                id: 'time',
                                dataIndex: 'time',
                                type: 'date',
                            },
                            {
                                id: 'views',
                                dataIndex: 'views',
                                type: 'number',
                                valueType: 'value',
                            },
                            {
                                id: 'votes',
                                dataIndex: 'votes',
                                type: 'number',
                                valueType: 'value',
                            },
                            {
                                id: 'follows',
                                dataIndex: 'follows',
                                type: 'number',
                                valueType: 'value',
                            },
                            {
                                id: 'comments',
                                dataIndex: 'comments',
                                type: 'number',
                                valueType: 'value',
                            },
                        ],
                    }}
                />
            </Spin>
        </div>
    )
}

const WorkspaceInsights = ({Wrapper = 'div'}) => {

    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const {
        organization_id,
        organization,
        getPeriod = NullAsDefault,
        setPeriod = NullAsDefault,
    } = useContext(WorkspaceContext)
    const groups = _.get(organization, 'groups', [])

    const period = getPeriod(WorkspaceFilterParams.PERIOD)

    const {path: root} = useRouteMatch()
    const location = useLocation()

    const {
        modal,
        refresh_token,
        handleAction = NullAsDefault,
    } = useAnnouncementForm({
        owner_type: 'organization',
        ownerItem: organization,
        groups,
        notifyListeners: {}
    })

    return (
        <Wrapper className="flex flex-col gap-6">

            {/* ─── 1. Thống kê tổng quan (4 card lớn) ───────────────── */}
            <Async
                {...{
                    apiInfo: organization_fetchStatistics_Api,
                    query: bindQueryParam({ id: organization_id }),
                    values: { period },
                }}>
                {({response, isLoading, handleAsyncAction = NullAsDefault}) => {
                    const { organization_summary, upcoming_events, upcoming_timetables, pending_stats } = getResponseItem(response)

                    // ── helpers ──────────────────────────────────────────
                    const totalPending = pending_stats
                        ? (pending_stats.pending_members_count ?? 0)
                        + (pending_stats.pending_course_registers_count ?? 0)
                        + (pending_stats.pending_hosts_count ?? 0)
                        : 0

                    // upcoming_events: event / course / examination
                    const mappedEvents = Array.isArray(upcoming_events)
                        ? upcoming_events.map((item) => {
                            const c = item.content
                            let description = ''
                            if (item.type === 'event') {
                                description = [c?.location_name, c?.event_type].filter(Boolean).join(' · ')
                            } else if (item.type === 'course') {
                                description = c?.academic_field ?? ''
                            } else if (item.type === 'examination') {
                                description = c?.description ?? ''
                            }
                            return {
                                id: item.id,
                                type: item.type,
                                title: c?.title ?? '',
                                description,
                                sub_label: null,
                                time_open: c?.time_open,
                                sort_key: c?.time_open,
                            }
                        })
                        : []

                    // upcoming_timetables: organization_timetable / course_class_timetable
                    const mappedTimetables = Array.isArray(upcoming_timetables)
                        ? upcoming_timetables.map((item) => {
                            const c = item.content
                            const timePart = [c?.from_time, c?.to_time].filter(Boolean).join(' – ')
                            const description = [timePart, c?.location_address].filter(Boolean).join(' · ')
                            const ownerType = c?.owner_type ?? (c?._type === 'organization_timetable' ? 'organization' : 'course_class')
                            const subLabel = ownerType !== 'course_class'
                                ? c?.group?.title ?? ''
                                : [c?.subject?.title, c?.teacher?.name].filter(Boolean).join(' · ')
                            return {
                                id: item.id,
                                type: c?._type ?? item.type,
                                title: c?.title ?? '',
                                description,
                                sub_label: subLabel,
                                time_open: item.from_date,
                                sort_key: item.from_date,
                            }
                        })
                        : []

                    // merge + sort tăng dần
                    const upcomingList = _.orderBy(
                        [...mappedEvents, ...mappedTimetables],
                        ['sort_key'],
                        ['asc']
                    )

                    return (
                        <React.Fragment>

                            {/* ── 1a. 4 card thống kê to ─────────────────────── */}
                            <Pure input={[organization_summary, isLoading, totalPending]}>
                                <WorkspaceInsightsMetaSummary
                                    summary={organization_summary}
                                    isLoading={isLoading}
                                    className="grid grid-cols-2 md:grid-cols-4 gap-4"
                                    Header={NullAsDefault}
                                    field_params={[
                                        {
                                            name: 'total_members',
                                            label: 'total members',
                                            extra_name: 'total_members_prev',
                                            icon: <FaUserGroup size={22} className="text-indigo-100"/>,
                                            description: t('this month'),
                                            border: 'border-t-3 border-blue-500',
                                        },
                                        {
                                            name: 'total_courses',
                                            label: 'total courses',
                                            extra_name: 'total_courses_prev',
                                            icon: <FaGraduationCap size={24} className="text-indigo-100"/>,
                                            description: t('member'),
                                            border: 'border-t-3 border-green-500',
                                        },
                                        {
                                            name: 'total_events',
                                            label: 'total events',
                                            extra_name: 'total_events_prev',
                                            icon: <BsCalendar2DayFill size={22} className="text-indigo-100"/>,
                                            description: t('upcoming'),
                                            border: 'border-t-3 border-orange-500',
                                        },
                                        {
                                            name: 'total_posts',
                                            label: 'total posts',
                                            extra_name: 'total_posts_prev',
                                            icon: <BsCalendar2DayFill size={22} className="text-indigo-100"/>,
                                            description: t('news post'),
                                            border: 'border-t-3 border-secondary-500',
                                        },
                                        // {
                                        //     // tổng pending từ pending_stats
                                        //     name: 'pending_approval',
                                        //     label: 'pending approval',
                                        //     // inject giá trị thực vào summary bên ngoài nếu cần,
                                        //     // hoặc truyền value trực tiếp qua description
                                        //     icon: <FaBell size={22} className="text-indigo-100"/>,
                                        //     description: t('needs attention'),
                                        //     border: 'border-t-3 border-secondary-500',
                                        //     // override value vì field này không có trong organization_summary
                                        //     value: totalPending,
                                        // },
                                    ]}
                                />
                            </Pure>

                            {/* ── 1b. Pending stats chi tiết (nhỏ, dưới 4 card) ── */}
                            {/* Mỗi tile giờ là 1 <Link> đi thẳng tới đúng màn xử lý (trước đây chỉ hiện số, không bấm
                                được, không rõ nghĩa - "Ban Quản Trị Chờ Duyệt" đặc biệt gây hiểu lầm là duyệt NHÂN SỰ
                                lãnh đạo, thực ra là "Host" = quan hệ LIÊN KẾT GIỮA CÁC TỔ CHỨC (chủ quản/trực thuộc/
                                liên kết ngang hàng, xem WorkspaceGoverningOrganizations.js/WorkspaceSubordinateUnits.js/
                                WorkspaceHostPeerMembers.js) - đã đổi nhãn cho đúng + thêm dòng giải thích dưới mỗi tile). */}
                            {pending_stats && (
                                <Pure input={[pending_stats, organization]}>
                                    <div
                                        className={classNames(
                                            "grid grid-cols-2 gap-3",
                                            Number(organization?.association_status) === 1 ? 'md:grid-cols-2' : 'md:grid-cols-3'
                                        )}>
                                        {[
                                            {
                                                key: 'pending_members_count',
                                                label: 'pending members',
                                                hint: 'pending members hint',
                                                to: '/workspace/member?rel=org-members',
                                                icon: <FaUserGroup size={18}/>,
                                                color: 'text-blue-500 bg-blue-50',
                                            },
                                            {
                                                key: 'pending_course_registers_count',
                                                label: 'pending enrollments',
                                                hint: 'pending enrollments hint',
                                                to: '/workspace/course?rel=register-for-course',
                                                icon: <FaGraduationCap size={18}/>,
                                                color: 'text-green-600 bg-green-50',
                                            },
                                            {
                                                key: 'pending_hosts_count',
                                                label: 'pending hosts',
                                                hint: 'pending hosts hint',
                                                to: '/workspace/member?rel=governing_organizations',
                                                icon: <BsPeopleFill size={18}/>,
                                                color: 'text-orange-500 bg-orange-50',
                                            },
                                        ].map(({key, label, hint, to, icon, color}) => {
                                            const count = pending_stats[key] ?? 0
                                            return (
                                                <Link
                                                    key={key}
                                                    to={to}
                                                    className="flex items-center gap-3 p-3 background border-t border-color-50 shadow-md rounded-xl no-underline hover:shadow-lg transition-shadow">
                                                    <div className={classNames('p-2 flex flex-center rounded-lg flex-shrink-0', color)}>
                                                        {icon}
                                                    </div>
                                                    <div className="flex flex-col flex-1 min-w-0">
                                                        <span className="font-medium text-gray-600 tracking-wide truncate">
                                                            {t(label)}
                                                        </span>
                                                        <span className="font-bold text-lg text-color-000 leading-snug">
                                                            {count}
                                                            {count > 0 && (
                                                                <span className="ml-1 font-medium text-2xs text-red-500 italic">
                                                                    {t('needs attention')}
                                                                </span>
                                                            )}
                                                        </span>
                                                        <span className="text-2xs text-color-300 leading-snug truncate">{t(hint)}</span>
                                                    </div>
                                                </Link>
                                            )
                                        })}

                                        {/* Sổ Gia Đình đang chờ giáo xứ xác nhận liên kết - LẤY LẠI ĐÚNG nguồn dữ liệu đã
                                            dùng cho badge sidebar "Sổ sách & Bí tích" (PendingHouseholdsBadge,
                                            WorkspaceBadges.js: familyBook_getFamilyBookStats_Api + total.pending) - TRƯỚC
                                            ĐÂY tile này gọi nhầm API Hộ Gia Đình Công Giáo cũ, màn hình quản lý của tính
                                            năng đó đã bị gỡ khỏi menu Workspace từ lâu (xem comment FAMILY_BOOK trong
                                            Workspace.js) nên bấm vào không đi đâu được - tile số liệu "chờ duyệt" mà
                                            không tài nào duyệt được. Đổi hẳn sang nguồn Sổ Gia Đình (tính năng thay thế
                                            thật sự) + link thẳng tới tab "Toàn giáo xứ > Đang chờ". */}
                                        {Number(organization?.association_status) === 1 && (
                                            <Async
                                                apiInfo={familyBook_getFamilyBookStats_Api}
                                                query={bindQueryParams([{type: getType(organization)}, {id: getId(organization)}])}
                                                values={{}}>
                                                {({response: fbResponse}) => {
                                                    const count = _.get(fbResponse, 'data.total.pending', 0)
                                                    return (
                                                        <Link
                                                            to="/workspace/family_books?rel=family-books&family-parish-status=-1"
                                                            className="flex items-center gap-3 p-3 background border-r-2 border-secondary-500 shadow-md rounded-xl no-underline hover:shadow-lg transition-shadow">
                                                            <div className="p-2 flex flex-center rounded-lg flex-shrink-0 text-indigo-500 bg-indigo-50">
                                                                <FamilyIcon size={18}/>
                                                            </div>
                                                            <div className="flex flex-col flex-1 min-w-0">
                                                                <span className="font-medium text-gray-600 tracking-wide truncate">
                                                                    {t('pending households')}
                                                                </span>
                                                                <span className="font-bold text-lg text-color-000 leading-snug">
                                                                    {count}
                                                                    {count > 0 && (
                                                                        <span className="ml-1 font-medium text-2xs text-red-500 italic">
                                                                            {t('needs attention')}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                                <span className="text-2xs text-color-300 leading-snug truncate">{t('pending households hint')}</span>
                                                            </div>
                                                        </Link>
                                                    )
                                                }}
                                            </Async>
                                        )}
                                    </div>
                                </Pure>
                            )}

                            {/* ── 2. Upcoming events (từ API) ─────────────────── */}
                            <div className="flex flex-col shadow-md background rounded-xl">
                                <div className="px-3 py-2 background-200 rounded-t-xl flex items-center gap-3">
                                    <span className="flex-1 font-medium text-sm text-gray-700 tracking-wide">
                                        {t('upcoming events')}
                                    </span>
                                </div>
                                <div className="flex flex-col divide-y divide-gray-200 rounded-b-xl">
                                    {_.isEmpty(upcomingList) ? (
                                        <div className="p-6 text-center text-color-300 text-sm tracking-wide">
                                            {t('no upcoming events')}
                                        </div>
                                    ) : upcomingList.map((e,i) => {
                                        const typeBadgeMap = {
                                            event:                    'bg-orange-50 text-orange-600',
                                            course:                   'bg-green-50 text-green-600',
                                            examination:              'bg-indigo-50 text-indigo-600',
                                            organization_timetable:   'bg-purple-50 text-purple-600',
                                            course_class_timetable:   'bg-sky-50 text-sky-600',
                                        }
                                        const typeBadge = typeBadgeMap[e.type] ?? 'bg-gray-100 text-gray-500'
                                        const displayDate = e.time_open

                                        return (
                                            <div
                                                key={`${e.type}-${e.id}`}
                                                className={classNames(
                                                    'p-3 flex items-start gap-3 hover:background-100',
                                                    i === upcomingList.length - 1 && 'rounded-b-xl'
                                                )}>
                                                {/* date badge */}
                                                <div
                                                    style={{width: 55, height: 55, padding: '0.375rem'}}
                                                    className="bg-secondary-50 rounded-xl text-secondary-600 flex flex-col flex-center flex-shrink-0">
                                                    <span className="font-bold text-xl leading-snug">
                                                        {dayjs(displayDate).format('DD')}
                                                    </span>
                                                    <span className="font-medium text-2xs uppercase leading-snug opacity-60">
                                                        {dayjs(displayDate).format('MMM')}
                                                    </span>
                                                </div>
                                                {/* content */}
                                                <div className="flex flex-col flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        {e.title && (
                                                            <span className="font-bold text-gray-800 tracking-wide truncate">
                                                                {e.title}
                                                            </span>
                                                        )}
                                                        <span
                                                            style={{ padding: '2px 8px' }}
                                                            className={classNames('text-2xs rounded-full font-medium flex-shrink-0', typeBadge)}>
                                                            {t(e.type)}
                                                        </span>
                                                    </div>
                                                    {!!e.sub_label && (
                                                        <span className="font-medium text-xs text-gray-600 italic tracking-wider truncate">
                                                            {e.sub_label}
                                                        </span>
                                                    )}
                                                    {!!e.description && (
                                                        <span className="text-xs text-color-300 tracking-wider truncate">
                                                            {e.description}
                                                        </span>
                                                    )}
                                                    <span className="text-xs text-color-400 tracking-wider">
                                                        {dayjs(displayDate).format('DD/MM/YYYY')}
                                                    </span>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        </React.Fragment>
                    )
                }}
            </Async>

            {/* ─── 2b. Tổng Quan Giáo Dân (Sổ Gia Đình) ──────────────── */}
            <WorkspaceParishOverviewSection organization={organization}/>

            {/* ─── 3. Announcements ──────────────────────────────────── */}
            <div className="flex flex-col shadow-md rounded-xl">
                <div className="px-3 py-2 background-200 rounded-t-xl flex items-center gap-3">
                    <span className="flex-1 font-medium text-sm text-gray-700 tracking-wide">
                        {t('newest announcements')}
                    </span>
                    {organization?.available_actions?.organization_manage_announcement && (
                        <Link
                            to={`/workspace/activities?rel=announcements`}
                            className="flex items-center gap-1 font-medium text-secondary-600 no-underline">
                            {t('view all')} <BsChevronRight size={12}/>
                        </Link>
                    )}
                </div>
                <div className="background rounded-b-xl">
                    <WorkspaceOrganizationAnnouncements
                        refreshToken={refresh_token}
                        header={false}
                    />
                </div>
            </div>

            <div className="h-20"/>
            {modal}
        </Wrapper>
    )
}

export default nest(withProps({lazy: true})(DelayRender), WorkspaceInsights)